import React, { useState, Fragment, useRef, useEffect, useMemo, useCallback } from 'react';
import { Transition, Dialog, Transition as DialogTransition } from '@headlessui/react';
import { DayPickerRangeController } from 'react-dates';
import moment from 'moment';
import { format, subMonths, subDays } from 'date-fns';
import { toast } from 'react-toastify';
import StandardPage from '../../components/StandardPage';
import Dropdown from '../../components/Dropdown';
import { FaRegClock } from 'react-icons/fa';
import { useApiKeys } from '../../context/api-keys';
import ExportControls from '../../components/ExportControls';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, Polyline, Polygon, useMap, ZoomControl } from 'react-leaflet';
import { latLngBounds } from 'leaflet';
import { useQueryMetadataContext } from '../../context/query-metadata';
import type { DropdownOption } from '../../context/query-metadata';
import { handleApiError } from '../../api-client/error_handler';
import axios from 'axios';
import { GeoJsonGeometry } from '../../utils/geofabric';
import { useOrganization } from '../../context/organization';
import {
  trackQueryDateModalClose,
  trackQueryDateModalOpen,
  trackQueryDatePreset,
  trackQueryDenseToggle,
  trackQueryLoadMore,
  trackQueryRun,
  trackQueryShowIndexToggle,
  trackQueryViewModeChange,
} from '../../analytics';

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './index.css';

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
  key: string;
}

const LAT_KEYS = ['lat', 'latitude', 'latdd', 'y'];
const LON_KEYS = ['lon', 'lng', 'long', 'longitude', 'londd', 'x'];

const normalizeKey = (key: string) => key.replace(/[\s_-]/g, '').toLowerCase();

const MAX_FILTER_LEVELS = 5;

const renderCellValue = (value: any) => {
  if (value === null || value === undefined) return '-';

  // allow primitives
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  // arrays -> join
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
  }

  // objects -> JSON
  if (typeof value === 'object') {
    // special case: GeoJSON geometry
    if (value?.type && Array.isArray(value?.coordinates)) {
      return `${value.type} (${value.coordinates.join(', ')})`;
    }
    return JSON.stringify(value);
  }

  return String(value);
};

const pickPreviewEntries = (obj: Record<string, any>) => {
  const priorityKeys = [
    'name',
    'id',
    'corridor_id',
    'congestion',
    'duration_in_traffic_min',
    'duration_min',
    'distance_km',
    'ratio',
    'provider',
    'timestamp',
    'type',
  ];

  const entries: [string, any][] = [];
  const used = new Set<string>();

  priorityKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      entries.push([key, obj[key]]);
      used.add(key);
    }
  });

  Object.entries(obj).forEach(([key, value]) => {
    if (used.has(key)) return;
    entries.push([key, value]);
  });

  return entries.filter(([, value]) => value !== undefined && value !== null).slice(0, 5);
};

const getPreviewTitle = (obj: Record<string, any>) =>
  obj.name || obj.id || obj.corridor_id || obj.type || 'Details';

const getCongestionStyle = (congestion?: string) => {
  switch (congestion) {
    case 'HEAVY':
      return { color: '#dc2626', weight: 5, dashArray: '8 6' };
    case 'MODERATE':
      return { color: '#f59e0b', weight: 4, dashArray: '6 6' };
    case 'SLOW':
      return { color: '#f97316', weight: 4, dashArray: '6 6' };
    case 'FREE_FLOW':
      return { color: '#16a34a', weight: 3, dashArray: '0' };
    default:
      return { color: '#2563eb', weight: 3, dashArray: '0' };
  }
};

const formatPreviewValue = (value: any) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') return Number.isFinite(value) ? value.toFixed(2).replace(/\.00$/, '') : String(value);
  return String(value);
};

const buildDefaultFilterPath = (options: DropdownOption[]): DropdownOption[] => {
  const path: DropdownOption[] = [];
  let levelOptions: DropdownOption[] | undefined = options;
  let depth = 0;
  while (levelOptions && levelOptions.length > 0 && depth < MAX_FILTER_LEVELS) {
    const next: DropdownOption = levelOptions[0];
    path.push(next);
    levelOptions = next.options;
    depth += 1;
  }
  return path;
};

const reconcileFilterPath = (
  current: DropdownOption[], 
  options: DropdownOption[]
): DropdownOption[] => {
  const path: DropdownOption[] = [];
  let levelOptions: DropdownOption[] | undefined = options;
  let depth = 0;
  while (levelOptions && levelOptions.length > 0 && depth < MAX_FILTER_LEVELS) {
    const prev = current[depth];
    const match: DropdownOption | undefined = prev
      ? levelOptions.find((opt: DropdownOption) => opt.value === prev.value)
      : undefined;
    const next: DropdownOption = match ?? levelOptions[0];
    path.push(next);
    levelOptions = next.options;
    depth += 1;
  }
  return path;
};

const normalizeGeoFeature = (row: any) => {
  // Convert traffic/admin flat rows that contain a geometry payload into Feature form
  // so map renderers can handle them consistently.
  if (row && row.type !== 'Feature' && row.geometry) {
    let geometry = row.geometry;
    try {
      if (typeof geometry === 'string') {
        geometry = JSON.parse(geometry);
      }
    } catch {}

    if (geometry?.type && geometry?.coordinates) {
      const { geometry: _ignoredGeometry, ...properties } = row;
      return {
        type: 'Feature',
        geometry,
        properties,
      };
    }
  }

  if (row?.type !== 'Feature') return row;

  let geometry = row.geometry;
  let properties = row.properties;

  try {
    if (typeof geometry === 'string') {
      geometry = JSON.parse(geometry);
    }
  } catch {}

  try {
    if (typeof properties === 'string') {
      properties = JSON.parse(properties);
    }
  } catch {}

  return {
    ...row,
    geometry,
    properties,
  };
};

const Query: React.FC = () => {
  const { apiKeys } = useApiKeys();
  const { getAccessTokenSilently } = useAuth0();
  const { data: queryMetadata, loading: metadataLoading, error: metadataError } = useQueryMetadataContext();
  const { organization } = useOrganization();
  const [locality, setLocality] = useState<DropdownOption | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<DropdownOption[]>([]);
  const [dateRange, setDateRange] = useState<DateRange[]>([
    { startDate: subMonths(new Date(), 1), endDate: new Date(), key: 'selection' }
  ]);
  const [results, setResults] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [paginationKey, setPaginationKey] = useState<string | null>(null);
  const [focusedInput, setFocusedInput] = useState<'startDate' | 'endDate' | null>('startDate');
  const [countrySearchQuery, setCountrySearchQuery] = useState<string>('');

  const [autoRun, setAutoRun] = useState<boolean>(false);
  const [dense, setDense] = useState<boolean>(false);
  const [showIndex, setShowIndex] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [mapStyle, setMapStyle] = useState<'light' | 'dark' | 'satellite'>('light');

  const localityOptions = queryMetadata.localities;
  const filterOptions = queryMetadata.filters;
  const columnMappings = queryMetadata.columnMappings;
  const isMetadataReady = filterOptions.length > 0 && localityOptions.length > 0 && selectedFilters.length > 0 && locality != null;
  const runDisabled = !isMetadataReady || isLoading;

  const getSelectedCategory = (): DropdownOption | null => selectedFilters[0] ?? filterOptions[0] ?? null;
  const getSelectedSource = (): DropdownOption | null => selectedFilters[1] ?? getSelectedCategory()?.options?.[0] ?? null;
  const getSelectedType = (): DropdownOption | null => {
    const category = getSelectedCategory();
    const source = getSelectedSource();
    if (!category) return null;
    const isInfrastructure = category.value === 'infrastructure';
    const isTrafficSource = (source?.value?.toString().toLowerCase() ?? '') === 'google';
    const index = isInfrastructure && !isTrafficSource ? 3 : 2;
    return selectedFilters[index] || null;
  };

  const selectedCategory = getSelectedCategory();
  const selectedSourceOption = getSelectedSource();
  const selectedTypeOption = getSelectedType();
  const sourceValue = selectedSourceOption?.value?.toString().toLowerCase() ?? '';
  const categoryValue = selectedCategory?.value?.toString().toLowerCase() ?? '';
  const isTrafficCategory = categoryValue === 'traffic' || (categoryValue === 'infrastructure' && sourceValue === 'google');
  const isMapPreferred = isTrafficCategory || sourceValue === 'osm';
  const isAcledSource = sourceValue === 'acled';

  const hasAcledLicense = useMemo(() => {
    if (!organization || !locality) return false;
    if (organization.has_all_countries) return true;
    const countries = Array.isArray(organization.acled_countries) ? organization.acled_countries : [];
    return countries.some((code) => typeof code === 'string' && code.toLowerCase() === locality.value.toLowerCase());
  }, [organization, locality]);

  useEffect(() => {
    if (filterOptions.length === 0) return;
    setSelectedFilters((prev) => {
      const next = prev.length === 0 ? buildDefaultFilterPath(filterOptions) : reconcileFilterPath(prev, filterOptions);
      const unchanged = prev.length === next.length && prev.every((opt, idx) => opt.value === next[idx]?.value);
      return unchanged ? prev : next;
    });
  }, [filterOptions]);

  useEffect(() => {
    if (localityOptions.length === 0) return;
    setLocality((prev) => {
      if (prev && localityOptions.some(opt => opt.value === prev.value)) return prev;
      return localityOptions[0];
    });
  }, [localityOptions]);

  useEffect(() => {
    if (metadataError) {
      toast.error(metadataError);
    }
  }, [metadataError]);

  const abortController = useRef<AbortController | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const lastAutoViewSource = useRef<string | null>(null);

  const navigate = useNavigate();

  const getLocalityOptions = () => localityOptions;

  const getOptionsAtLevel = (level: number, selected: DropdownOption[]): DropdownOption[] => {
    let options: DropdownOption[] | undefined = filterOptions;
    for (let i = 0; i < level; i++) {
      const selectedOption = selected[i];
      if (!selectedOption || !options) return [];
      const found: DropdownOption | undefined = options.find((opt: DropdownOption) => opt.value === selectedOption.value);
      options = found?.options;
    }
    return options ?? [];
  };

  const handleFilterChange = (level: number, option: DropdownOption) => {
    const updated = [...selectedFilters.slice(0, level), option];
    for (let i = level + 1; i < MAX_FILTER_LEVELS; i++) {
      const options = getOptionsAtLevel(i, updated);
      if (options.length > 0) {
        updated.push(options[0]);
      } else {
        break;
      }
    }
    setSelectedFilters(updated);
  };

  const prevParams = useRef({
    category: null as DropdownOption | null,
    locality: null as DropdownOption | null,
    source: null as DropdownOption | null,
    type: null as DropdownOption | null,
    dateRange
  });

  const generateSearchParams = (paginationKey?: string) => {
    const params = new URLSearchParams();
    const source = getSelectedSource();
    const type = getSelectedType();
    if (source?.value) params.append('source', source.value);
    if (type?.value) params.append('type', type.value);
    if (dateRange[0].startDate) {
      params.append('from_date', format(dateRange[0].startDate, 'yyyy-MM-dd'));
    }
    if (dateRange[0].endDate) {
      params.append('to_date', format(dateRange[0].endDate, 'yyyy-MM-dd'));
    }
    if (paginationKey) params.append('pagination_key', paginationKey);
    return params;
  };

  const validateFields = () => {
    if (filterOptions.length === 0 || localityOptions.length === 0) {
      toast.error('Filters are still loading. Please wait a moment.');
      return false;
    }
    if (getSelectedCategory() == null) return toast.error('Please select category'), false;
    if (getSelectedSource() == null) return toast.error('Please select source'), false;
    if (locality == null) return toast.error('Please select locality'), false;
    if (!apiKeys?.[0]?.api_key) return toast.error('Missing API key'), false;
    return true;
  };

  const handleApiCall = async (paginationKey?: string, isLoadMore?: boolean) => {
    if (!validateFields()) return;

    if (isLoading && abortController.current) {
      abortController.current.abort();
      setIsLoading(false);
      return;
    }

    if (isLoadMore) setIsLoadingMore(true); else setIsLoading(true);
    abortController.current = new AbortController();

    try {
      const token = await getAccessTokenSilently();
      const category = getSelectedCategory();
      const source = getSelectedSource();
      const type = getSelectedType();
      if (!category || !locality || !source) return;
      const apiCategory = category.value === 'infrastructure' && source.value?.toString().toLowerCase() === 'google'
        ? 'traffic'
        : category.value;
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/${apiCategory}/${locality.value}?${generateSearchParams(paginationKey)}`,
        {
          headers: { 'x-api-key': apiKeys[0].api_key, Authorization:  `Bearer ${token}` },
          signal: abortController.current.signal
        }
      );
      if (abortController.current.signal.aborted) return;

      if (
        prevParams.current.category !== category ||
        prevParams.current.locality !== locality ||
        prevParams.current.source !== source ||
        prevParams.current.type !== type ||
        prevParams.current.dateRange !== dateRange
      ) {
        setResults([]);
        prevParams.current = { category, locality, source, type, dateRange };
      }

      const normalizedEvents = data.events.map(normalizeGeoFeature);
      setResults(prev =>
        paginationKey
          ? [...prev, ...normalizedEvents]
          : (normalizedEvents.length === 0 ? [] : normalizedEvents)
      );
      setPaginationKey(data.next_page_cursor ? data.next_page_cursor : null);
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError' || (error as any).code === 'ERR_CANCELED') return;
    
        const msg = error.message || '';
        // Detect ACLED permission error and show an actionable toast
        if (/Access to ACLED data for this country is not permitted/i.test(msg)) {
          toast.dismiss(); // avoid stacking
          toast.error(
            <div className="flex items-center gap-2 flex-wrap">
              <span>
                <span className="font-semibold">ACLED license required:</span>
                <p className="text-sm text-slate-700">
                  Access to ACLED data for this country is not permitted.
                </p>
              </span>
              <button
                onClick={() => {
                  navigate('/acled-license');
                  toast.dismiss();
                }}
                className="text-sm font-medium text-blue-600 hover:underline whitespace-nowrap"
              >
                View Plans →
              </button>
            </div>,
            {
              autoClose: 6000,
              closeOnClick: true,
              hideProgressBar: false,
              icon: <span className="text-lg">🔒</span>,
              toastId: 'acled-license-required',
            }
          );          
        } else {
          toast.error(handleApiError(error));
        }
      }
    } finally {
      if (isLoadMore) setIsLoadingMore(false); else setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!autoRun || !isMetadataReady) return;
    const t = setTimeout(() => handleApiCall(undefined, false), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, locality, selectedFilters, dateRange, isMetadataReady]);

  useEffect(() => {
    if (!selectedSourceOption) return;
    const key = `${categoryValue}-${sourceValue}`;
    if (lastAutoViewSource.current === key) return;
    lastAutoViewSource.current = key;
    setViewMode(isMapPreferred ? 'map' : 'list');
  }, [isMapPreferred, selectedSourceOption, sourceValue, categoryValue]);

  const handleDatesChange = ({ startDate, endDate }: { startDate: moment.Moment | null, endDate: moment.Moment | null }) => {
    setDateRange([{ startDate: startDate?.toDate() || null, endDate: endDate?.toDate() || null, key: 'selection' }]);
  };

  const getDateRangeDays = () => {
    const start = dateRange[0].startDate;
    const end = dateRange[0].endDate;
    if (!start || !end) return undefined;
    const diffMs = end.getTime() - start.getTime();
    if (!Number.isFinite(diffMs) || diffMs < 0) return undefined;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1;
  };

  const formatDateRange = () => {
    const start = dateRange[0].startDate;
    const end = dateRange[0].endDate;
    if (!start || !end) return 'Please select date';
    return `${format(start, 'MMM dd, yyyy')} – ${format(end, 'MMM dd, yyyy')}`;
  };

  const loadMoreResults = () => {
    if (paginationKey) {
      trackQueryLoadMore({ current_count: results.length });
      handleApiCall(paginationKey, true);
    }
  };

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!paginationKey) return;

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !isLoadingMore) loadMoreResults();
      },
      { rootMargin: '400px 0px' }
    );
    io.observe(sentinelRef.current);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationKey, isLoadingMore]);

  const mapColumns = (dataset: string, resultsArr: any[]) => {
    const mapping = columnMappings[dataset];
    if (!mapping) return resultsArr;
  
    return resultsArr.map((result: any) => {
      const mappedResult: Record<string, unknown> = {};
  
      (Object.keys(mapping) as Array<keyof typeof mapping>).forEach(key => {
        // result might be Feature or object => both are indexable in TS only if casted
        const src = result as Record<string, unknown>;
        mappedResult[mapping[key]] = src[key] ?? '-';
      });
  
      return mappedResult;
    });
  };  

  const datasetKey = `${prevParams.current.category?.value ?? 'conflict'}-${prevParams.current.source?.value ?? 'acled'}`;
  const formattedResults = useMemo(() => mapColumns(datasetKey, results), [datasetKey, results]);
  const columns = useMemo(() => (formattedResults[0] ? Object.keys(formattedResults[0]) : []), [formattedResults]);

  const extractLatLon = (row: any): { lat: number; lon: number } | null => {
    // OSM Feature
    if ((row as any)?.type === 'Feature') {
      const geom = (row as any)?.geometry as GeoJsonGeometry | undefined;
  
      if (geom?.type === 'Point') {
        const [lon, lat] = geom.coordinates;
        if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon };
      }
  
      // For Lines/Polygons => no single point.
      return null;
    }
  
    // Flat lat/lon keys fallback
    const obj = row as Record<string, unknown>;
    const latKey = Object.keys(obj).find((key) => LAT_KEYS.includes(normalizeKey(key)));
    const lonKey = Object.keys(obj).find((key) => LON_KEYS.includes(normalizeKey(key)));
    if (!latKey || !lonKey) return null;
  
    const lat = typeof obj[latKey] === 'number' ? (obj[latKey] as number) : parseFloat(String(obj[latKey]));
    const lon = typeof obj[lonKey] === 'number' ? (obj[lonKey] as number) : parseFloat(String(obj[lonKey]));
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  
    return { lat, lon };
  };
  
  const toLatLng = (coord: [number, number]): [number, number] => {
    // GeoJSON: [lon, lat] -> Leaflet expects [lat, lon]
    return [coord[1], coord[0]];
  };
  
  const extractLineCoords = (row: any): [number, number][][] | null => {
    if (row?.type !== 'Feature') return null;
    const geom = row?.geometry as GeoJsonGeometry | undefined;
    if (!geom) return null;
  
    if (geom.type === 'LineString') {
      return [geom.coordinates.map(toLatLng)];
    }
  
    if (geom.type === 'MultiLineString') {
      return geom.coordinates.map((line) => line.map(toLatLng));
    }
  
    return null;
  };
  
  const extractPolygonCoords = (row: any): [number, number][][][] | null => {
    if (row?.type !== 'Feature') return null;
    const geom = row?.geometry as GeoJsonGeometry | undefined;
    if (!geom) return null;
  
    if (geom.type === 'Polygon') {
      return [geom.coordinates.map((ring) => ring.map(toLatLng))];
    }
  
    if (geom.type === 'MultiPolygon') {
      return geom.coordinates.map((poly) => poly.map((ring) => ring.map(toLatLng)));
    }
  
    return null;
  };  

  const mapLayers = useMemo(() => {
    const points: { lat: number; lon: number; preview: [string, any][] }[] = [];
    const lines: { coords: [number, number][][]; preview: [string, any][]; style: { color: string; weight: number; dashArray: string } }[] = [];
    const polygons: { coords: [number, number][][][]; preview: [string, any][], congestionColor: string }[] = [];
    const bounds = latLngBounds([]);

    results.forEach((row, idx) => {
      const previewRow = formattedResults[idx] || row;

      const basePreview =
        row?.properties && typeof row.properties === 'object'
          ? row.properties
          : previewRow;

      const congestionColor =
        row.properties?.congestion === 'HEAVY' ? '#dc2626' :
        row.properties?.congestion === 'MODERATE' ? '#f59e0b' :
        row.properties?.congestion === 'SLOW' ? '#f97316' :
        '#16a34a';
  
      const previewEntries = pickPreviewEntries(basePreview as Record<string, any>);
      const lineStyle = getCongestionStyle(row.properties?.congestion);
  
      // POINT
      const loc = extractLatLon(row);
      if (loc) {
        points.push({ lat: loc.lat, lon: loc.lon, preview: previewEntries });
        bounds.extend([loc.lat, loc.lon]);
        return;
      }
  
      // LINE
      const line = extractLineCoords(row);
      if (line) {
        lines.push({ coords: line, preview: previewEntries, style: lineStyle });
        line.forEach((segment) => {
          segment.forEach((coord) => bounds.extend(coord));
        });
        return;
      }
  
      // POLYGON
      const poly = extractPolygonCoords(row);
      if (poly) {
        polygons.push({ coords: poly, preview: previewEntries, congestionColor });
        poly.forEach((rings) => {
          rings.forEach((ring) => {
            ring.forEach((coord) => bounds.extend(coord));
          });
        });
      }
    });
  
    const hasBounds = bounds.isValid();
    return { points, lines, polygons, bounds: hasBounds ? bounds : null };
  }, [results, formattedResults]);  

  const hasMapData =
    mapLayers.points.length > 0 ||
    mapLayers.lines.length > 0 ||
    mapLayers.polygons.length > 0;

  useEffect(() => {
    if (viewMode === 'map' && !hasMapData && !isMapPreferred) {
      setViewMode('list');
    }
  }, [viewMode, hasMapData, isMapPreferred]);

  const shouldShowDates = useMemo(() => {
    if (!selectedSourceOption) return false;
    const source = selectedSourceOption.value?.toString() ?? '';
    if (source === 'OSM') return false;
    if (source === 'population-count' || source === 'density') return false;
    return true;
  }, [selectedSourceOption]);

  const quickSetDates = useCallback((range: 'week' | 'month' | 'year' | number) => {
    if (typeof range === 'number') {
      trackQueryDatePreset({ preset: 'custom_days', range_days: range });
    } else {
      trackQueryDatePreset({ preset: range });
    }
    if (range === 'week') {
      const today = moment();
      const s = today.clone().subtract(1, 'weeks').startOf('week');
      const e = today.clone().subtract(1, 'weeks').endOf('week');
      setDateRange([{ startDate: s.toDate(), endDate: e.toDate(), key: 'selection' }]);
    } else if (range === 'month') {
      setDateRange([{ startDate: subMonths(new Date(), 1), endDate: new Date(), key: 'selection' }]);
    } else if (range === 'year') {
      setDateRange([{ startDate: subMonths(new Date(), 12), endDate: new Date(), key: 'selection' }]);
    } else if (typeof range === 'number') {
      const endDate = new Date();
      const startDate = subDays(endDate, range);
      setDateRange([{ startDate, endDate, key: 'selection' }]);
    }
  }, []);

  const ActiveChips = () => {
    if (!selectedCategory || !selectedSourceOption || !locality) return null;
    const shouldShowDates = dateRange[0].startDate && dateRange[0].endDate && selectedSourceOption.value !== 'OSM';

    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
          {selectedCategory.label}
        </span>
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
          {selectedSourceOption.label}
        </span>
        {selectedTypeOption?.label && (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
            {selectedTypeOption.label}
          </span>
        )}
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
          {locality.label}
        </span>
        {shouldShowDates && (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
            {formatDateRange()}
          </span>
        )}
      </div>
    );
  };

  const renderResultsTable = () => {
    if (formattedResults.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-full bg-slate-100">
            <svg className="h-5 w-5 text-slate-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm1 15h-2v-2h2Zm0-4h-2V7h2Z"/></svg>
          </div>
          <h3 className="text-sm font-semibold text-slate-800">No results</h3>
          <p className="mt-1 text-sm text-slate-500">Try adjusting your filters or date range, then run the query.</p>
        </div>
      );
    }

    return (
      <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-md">
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y divide-slate-200 ${dense ? 'text-[13px]' : 'text-sm'}`}>
            <thead className="bg-gradient-to-r from-indigo-500 via-violet-600 to-fuchsia-500">
              <tr>
                {showIndex && (
                  <th className="sticky left-0 bg-indigo-600/10 backdrop-blur px-4 py-3 text-left font-semibold uppercase tracking-wider text-white">
                    #
                  </th>
                )}
                {columns.map((column, i) => (
                  <th key={i} className="px-6 py-3 text-left font-semibold uppercase tracking-wider text-white whitespace-nowrap">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {formattedResults.map((row: any, idx: number) => (
                <tr key={idx} className="transition hover:bg-slate-50">
                  {showIndex && (
                    <td className="sticky left-0 bg-white px-4 py-3 text-slate-600">{idx + 1}</td>
                  )}
                  {columns.map((c) => (
                    <td key={c} className="px-6 py-3 whitespace-nowrap">
                      {renderCellValue(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* sentinel for infinite scroll */}
        {paginationKey && (
          <div ref={sentinelRef} className="h-10 w-full" />
        )}
      </div>
    );
  };

  const FitBounds = ({ bounds }: { bounds: ReturnType<typeof latLngBounds> | null }) => {
    const map = useMap();
    useEffect(() => {
      if (!bounds) return;
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }, [map, bounds]);
    return null;
  };

  const renderMapView = () => {
    const initialCenter: [number, number] =
      mapLayers.points.length > 0
        ? [mapLayers.points[0].lat, mapLayers.points[0].lon]
        : [33.8938, 35.5018]; // fallback

    return (
      <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white shadow-md">
        {!hasMapData && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-[1000] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white/90 px-4 py-3 text-center text-sm text-slate-700 shadow-lg ring-1 ring-slate-200 backdrop-blur">
            Run a query to load map features.
          </div>
        )}
        <div className="absolute right-4 top-4 z-[1000] flex items-center gap-1 rounded-full bg-white/90 p-1 text-xs font-semibold text-slate-700 shadow-lg ring-1 ring-slate-200 backdrop-blur">
          {(['light', 'dark', 'satellite'] as const).map((style) => (
            <button
              key={style}
              className={`rounded-full px-3 py-1 transition ${
                mapStyle === style ? 'bg-slate-900 text-white shadow' : 'text-slate-700 hover:bg-slate-100'
              }`}
              onClick={() => setMapStyle(style)}
            >
              {style === 'light' ? 'Light' : style === 'dark' ? 'Dark' : 'Satellite'}
            </button>
          ))}
        </div>
        <MapContainer
          center={initialCenter}
          zoom={4}
          className="h-[520px] w-full query-map"
          scrollWheelZoom
          zoomControl={false}
        >
          <FitBounds bounds={mapLayers.bounds} />
          <ZoomControl position="bottomright" />
          <TileLayer
            attribution={
              mapStyle === 'satellite'
                ? 'Tiles &copy; Esri'
                : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }
            url={
              mapStyle === 'dark'
                ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                : mapStyle === 'satellite'
                ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            }
          />
          {mapStyle === 'satellite' && (
            <TileLayer
              attribution='Labels &copy; Esri'
              url='https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
            />
          )}
          {/* POINTS */}
          {mapLayers.points.map((point, idx) => (
            <CircleMarker
              key={`pt-${point.lat}-${point.lon}-${idx}`}
              center={[point.lat, point.lon]}
              radius={6}
              pathOptions={{ color: '#2563eb', fillColor: '#2563eb', fillOpacity: 0.8 }}
            >
              <Tooltip direction="top" offset={[0, -6]} sticky>
                <div className="min-w-[180px] space-y-2 rounded-lg bg-white/95 p-2 shadow-lg ring-1 ring-slate-200">
                  <div className="text-xs font-semibold text-slate-900">{getPreviewTitle(Object.fromEntries(point.preview))}</div>
                  <div className="space-y-1">
                    {point.preview.map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between gap-3 text-xs">
                        <span className="text-slate-500">{label}</span>
                        <span className="font-medium text-slate-800">{formatPreviewValue(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Tooltip>
            </CircleMarker>
          ))}

          {/* LINES */}
          {mapLayers.lines.map((line, idx) => (
            <React.Fragment key={`ln-${idx}`}>
              {line.coords.map((coords, i) => (
                <Polyline
                  key={`ln-${idx}-${i}`}
                  positions={coords}
                  pathOptions={{ color: line.style.color, weight: line.style.weight, opacity: 0.85, dashArray: line.style.dashArray }}
                >
                  <Tooltip sticky>
                    <div className="min-w-[200px] space-y-2 rounded-lg bg-white/95 p-2 shadow-lg ring-1 ring-slate-200">
                      <div className="text-xs font-semibold text-slate-900">{getPreviewTitle(Object.fromEntries(line.preview))}</div>
                      <div className="space-y-1">
                        {line.preview.map(([label, value]) => (
                          <div key={label} className="flex items-center justify-between gap-3 text-xs">
                            <span className="text-slate-500">{label}</span>
                            <span className="font-medium text-slate-800">{formatPreviewValue(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Tooltip>
                </Polyline>
              ))}
            </React.Fragment>
          ))}

          {/* POLYGONS */}
          {mapLayers.polygons.map((poly, idx) => (
            <React.Fragment key={`pg-${idx}`}>
              {poly.coords.map((rings, i) => (
                <Polygon
                  key={`pg-${idx}-${i}`}
                  positions={rings}
                  pathOptions={{
                    color: poly.congestionColor,
                    weight: 2,
                    opacity: 0.7,
                    fillOpacity: 0.12
                  }}
                >
                  <Tooltip sticky>
                    <div className="min-w-[200px] space-y-2 rounded-lg bg-white/95 p-2 shadow-lg ring-1 ring-slate-200">
                      <div className="text-xs font-semibold text-slate-900">{getPreviewTitle(Object.fromEntries(poly.preview))}</div>
                      <div className="space-y-1">
                        {poly.preview.map(([label, value]) => (
                          <div key={label} className="flex items-center justify-between gap-3 text-xs">
                            <span className="text-slate-500">{label}</span>
                            <span className="font-medium text-slate-800">{formatPreviewValue(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Tooltip>
                </Polygon>
              ))}
            </React.Fragment>
          ))}
        </MapContainer>
      </div>
    );
  };

  return (
    <StandardPage title='Query' marginBottom={40}>
      <div className="sticky top-[3.5rem] z-40 mb-4 rounded-xl border border-slate-200 bg-white/80 backdrop-blur p-3 sm:p-4 shadow-sm">
        {(!isMetadataReady || metadataLoading) && (
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-sm">
              <span className="text-sm font-semibold text-slate-600">Category</span>
              <div className="h-2 w-16 animate-pulse rounded-full bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200" />
            </div>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-12 w-44 rounded-xl bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 animate-pulse"
              />
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          {/* Dynamic nested dropdowns + locality */}
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: MAX_FILTER_LEVELS }).map((_, level) => {
              const options = getOptionsAtLevel(level, selectedFilters);
              return (
                <React.Fragment key={level}>
                  {level === 1 && (
                    <Dropdown
                      selectedOption={locality}
                      options={getLocalityOptions()}
                      onChange={setLocality}
                      size="small"
                      setSearchQuery={setCountrySearchQuery}
                      searchQuery={countrySearchQuery}
                    />
                  )}
                  {options.length > 0 && (
                    <Dropdown
                      selectedOption={selectedFilters[level] ?? null}
                      options={options}
                      onChange={(opt) => handleFilterChange(level, opt)}
                      size="small"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Date picker trigger + quick presets */}
          {shouldShowDates && (
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => {
                  trackQueryDateModalOpen();
                  setModalOpen(true);
                }}
                className="inline-flex items-center rounded-lg bg-white h-10 px-3 text-left shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
              >
                <FaRegClock className="mr-2" />
                <span className="text-sm">{formatDateRange()}</span>
              </button>

              <div className="hidden md:flex items-center gap-1">
                <button onClick={() => quickSetDates('week')} className="rounded-full bg-slate-100 px-3 py-1 text-xs hover:bg-slate-200">Last Week</button>
                <button onClick={() => quickSetDates('month')} className="rounded-full bg-slate-100 px-3 py-1 text-xs hover:bg-slate-200">Last Month</button>
                <button onClick={() => quickSetDates('year')} className="rounded-full bg-slate-100 px-3 py-1 text-xs hover:bg-slate-200">Last Year</button>
                <button onClick={() => quickSetDates(7)} className="rounded-full bg-slate-100 px-3 py-1 text-xs hover:bg-slate-200">7d</button>
                <button onClick={() => quickSetDates(30)} className="rounded-full bg-slate-100 px-3 py-1 text-xs hover:bg-slate-200">30d</button>
              </div>
            </div>
          )}
        </div>

        {isAcledSource && !hasAcledLicense && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-semibold">ACLED usage notice</p>
                <p className="mt-1 text-sm text-amber-800">
                  Querying ACLED datapoints without a license is prohibitively expensive. If you need substantial ACLED
                  data, the recommended approach is to purchase an ACLED license.
                </p>
              </div>
              <button
                onClick={() => navigate('/acled-license')}
                className="inline-flex items-center rounded-full border border-amber-300 bg-white px-4 py-1.5 text-xs font-semibold text-amber-900 shadow-sm hover:bg-amber-100"
              >
                View ACLED license
              </button>
            </div>
          </div>
        )}

        {/* chips + controls row */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <ActiveChips />
          <div className="flex items-center gap-2">
            <label className="hidden sm:flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1 text-xs text-slate-600 ring-1 ring-slate-200">
              <input
                type="checkbox"
                className="accent-blue-600"
                checked={dense}
                onChange={(e) => {
                  const next = e.target.checked;
                  setDense(next);
                  trackQueryDenseToggle({ enabled: next });
                }}
              />
              Dense rows
            </label>
            <label className="hidden sm:flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1 text-xs text-slate-600 ring-1 ring-slate-200">
              <input
                type="checkbox"
                className="accent-blue-600"
                checked={showIndex}
                onChange={(e) => {
                  const next = e.target.checked;
                  setShowIndex(next);
                  trackQueryShowIndexToggle({ enabled: next });
                }}
              />
              Row #
            </label>

            <button
              onClick={() => {
                trackQueryRun({
                  category: getSelectedCategory()?.value,
                  source: getSelectedSource()?.value,
                  type: getSelectedType()?.value ?? undefined,
                  locality: locality?.value,
                  date_range_days: getDateRangeDays(),
                  view_mode: viewMode,
                  trigger: 'manual',
                });
                handleApiCall();
              }}
              disabled={runDisabled}
              className={`inline-flex items-center justify-center rounded-full px-4 h-9 font-semibold text-white shadow-lg transition transform ${runDisabled ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02]'} ${isLoading ? 'from-blue-400 to-blue-600 cursor-wait' : 'from-blue-500 to-blue-700'} bg-gradient-to-r`}
            >
              {isLoading ? (
                <svg aria-hidden="true" className="mr-2 inline h-4 w-4 animate-spin" viewBox="0 0 100 101" fill="none">
                  <path d="M100 50.59C100 78.21 77.61 100.59 50 100.59C22.39 100.59 0 78.21 0 50.59C0 22.98 22.39 0.59 50 0.59C77.61 0.59 100 22.98 100 50.59Z" fill="#FFF" />
                  <path d="M93.968 39.041C96.393 38.404 97.862 35.912 97.008 33.554C95.293 28.823 92.871 24.369 89.817 20.348C85.845 15.119 80.883 10.724 75.212 7.413C69.542 4.102 63.275 1.94 56.77 1.051C51.767 0.368 46.698 0.447 41.735 1.279C39.261 1.693 37.813 4.198 38.45 6.623C39.087 9.049 41.569 10.472 44.05 10.107C47.851 9.548 51.719 9.527 55.54 10.049C60.864 10.777 65.993 12.546 70.633 15.255C75.274 17.965 79.335 21.562 82.585 25.841C84.918 28.912 86.8 32.291 88.181 35.876C89.083 38.216 91.542 39.678 93.968 39.041Z" fill="#1D4ED8" />
                </svg>
              ) : null}
              {isLoading ? 'Running…' : 'Run query'}
            </button>
          </div>
        </div>
      </div>

      {/* export summary */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-600">
          {formattedResults.length > 0 && (
            <span>
              Showing <span className="font-semibold text-slate-800">{formattedResults.length}</span>
              {paginationKey && <span> (more available)</span>}
            </span>
          )}
          {viewMode === 'map' && (
            <span className="ml-2 text-xs text-slate-500">Hover any dot to peek at event details.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasMapData && (
            <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1 text-xs font-semibold text-slate-600">
              <button
                className={`rounded-full px-3 py-1 transition ${viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : ''}`}
                onClick={() => {
                  if (viewMode !== 'list') {
                    setViewMode('list');
                    trackQueryViewModeChange({ view_mode: 'list' });
                  }
                }}
              >
                List View
              </button>
              <button
                className={`rounded-full px-3 py-1 transition ${viewMode === 'map' ? 'bg-white text-slate-900 shadow-sm' : ''}`}
                onClick={() => {
                  if (viewMode !== 'map') {
                    setViewMode('map');
                    trackQueryViewModeChange({ view_mode: 'map' });
                  }
                }}
                disabled={!hasMapData}
              >
                Map View
              </button>
            </div>
          )}
          {isMetadataReady && locality && selectedCategory && (
            <ExportControls
              category={selectedCategory}
              locality={locality}
              generateSearchParams={generateSearchParams}
              fetchRecordsCount={results.length}
            />
          )}
        </div>
      </div>

      {viewMode === 'map' ? renderMapView() : renderResultsTable()}

      {/* Fallback button if user prefers manual paging */}
      {paginationKey && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={loadMoreResults}
            className="rounded-full bg-gradient-to-r from-blue-500 to-blue-700 px-5 py-2 text-sm font-semibold text-white shadow-lg transition hover:scale-105"
          >
            {isLoadingMore ? 'Loading…' : 'Load More'}
          </button>
        </div>
      )}

      {/* Date modal */}
      <DialogTransition.Root show={modalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            trackQueryDateModalClose();
            setModalOpen(false);
          }}
        >
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="mb-4 text-lg font-semibold text-slate-900">
                  Select Date Range
                </Dialog.Title>
                <div className="mb-4 text-center">
                  <h4 className="text-lg font-medium text-slate-700">{formatDateRange()}</h4>
                </div>
                <DayPickerRangeController
                  startDate={moment(dateRange[0].startDate)}
                  endDate={moment(dateRange[0].endDate)}
                  maxDate={moment(dateRange[0].endDate)}
                  onDatesChange={handleDatesChange}
                  focusedInput={focusedInput}
                  onFocusChange={(fi) => setFocusedInput(fi || 'startDate')}
                  numberOfMonths={2}
                  isOutsideRange={() => false}
                  noBorder
                  initialVisibleMonth={() => moment(dateRange[0].startDate)}
                />
                <div className="ml-5 mb-4 mt-3 flex flex-wrap items-center gap-2">
                  <button onClick={() => quickSetDates('week')} className="rounded bg-slate-100 px-3 py-1 text-sm hover:bg-slate-200">Last Week</button>
                  <button onClick={() => quickSetDates('month')} className="rounded bg-slate-100 px-3 py-1 text-sm hover:bg-slate-200">Last Month</button>
                  <button onClick={() => quickSetDates('year')} className="rounded bg-slate-100 px-3 py-1 text-sm hover:bg-slate-200">Last Year</button>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Days ago"
                      min="1"
                      className="w-24 rounded border border-slate-300 px-2 py-1 text-sm"
                      onChange={(e) => {
                        const days = parseInt(e.target.value, 10);
                        if (!isNaN(days) && days > 0) quickSetDates(days);
                      }}
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={() => {
                      trackQueryDateModalClose();
                      setModalOpen(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </DialogTransition.Root>
    </StandardPage>
  );
};

export default Query;
